/**
 * carryGeminiLiveTurnDetection
 *
 * What a deployment's stored `geminiTurnDetection` becomes when its Gemini Live
 * model changes. The one rule for it — the voice editor applies it the moment
 * the model is picked, so the panel shows what will be sent, and the backend
 * applies it on every settings write, so a switch through the public API, the
 * CLI or the SDK lands the same way.
 *
 * WHY A SWITCH MOVES A STORED VALUE AT ALL: each model has its own default turn
 * detection (`GeminiLiveCapabilities.defaultTurnDetection`), and a stored block
 * usually exists only because the editor seeded the whole OLD default the first
 * time one field was touched. Carried unchanged onto the new model, that seed
 * runs the old model's tuning on the new one — on 3.8, END_SENSITIVITY_HIGH
 * splits a caller who pauses inside an order number into two turns, and the
 * tool is called with the second half.
 *
 * THE RULE IS PER FIELD. A field still equal to the OLD model's default was
 * never chosen, so it takes the NEW model's default; a field that differs from
 * it is the user's choice and is kept. Judging the block as a whole would keep
 * every untouched field on any deployment that customised one — a 3.1 row that
 * only turned interruptions off would still carry END_HIGH onto 3.8.
 *
 * Absent stays absent: a deployment that stores none already follows its model
 * (`resolveGeminiLiveTurnDetection`), and writing a value would pin it there.
 */

import { GEMINI_LIVE_MODELS, type GeminiLiveModelId } from "./GeminiLiveModels.schema";
import { type GeminiLiveTurnDetection } from "./GeminiLiveTurnDetection.schema";

/** Every field a turn-detection block can carry — a new field fails typecheck here until listed. */
const TURN_DETECTION_FIELDS = {
  startSensitivity: true,
  endSensitivity: true,
  prefixPaddingMs: true,
  silenceDurationMs: true,
  interruptResponse: true
} as const satisfies Record<keyof GeminiLiveTurnDetection, true>;

type Field = keyof typeof TURN_DETECTION_FIELDS;

const FIELDS = Object.keys(TURN_DETECTION_FIELDS) as Field[];

export function carryGeminiLiveTurnDetection(
  stored: GeminiLiveTurnDetection | undefined,
  from: GeminiLiveModelId,
  to: GeminiLiveModelId
): GeminiLiveTurnDetection | undefined {
  if (stored === undefined || from === to) return stored;

  const oldDefault = GEMINI_LIVE_MODELS[from].capabilities.defaultTurnDetection;
  const newDefault = GEMINI_LIVE_MODELS[to].capabilities.defaultTurnDetection;

  const carried: GeminiLiveTurnDetection = { ...stored };
  for (const field of FIELDS) {
    if (stored[field] !== oldDefault[field]) continue;
    const next = newDefault[field];
    if (next === undefined) {
      delete carried[field];
    } else {
      Object.assign(carried, { [field]: next });
    }
  }
  return carried;
}

/** Whether two turn-detection blocks configure the same thing, field by field. */
export function sameGeminiLiveTurnDetection(
  a: GeminiLiveTurnDetection,
  b: GeminiLiveTurnDetection
): boolean {
  return FIELDS.every((field) => a[field] === b[field]);
}
